<template>
  <div class="form-builder-page">
    <!-- Header with save/cancel -->
    <div class="form-builder-header">
      <h3>
        {{
          isEditing ? t('registration.editForm') : t('registration.createForm')
        }}
      </h3>
    </div>

    <!-- Tab Navigation -->
    <div class="tab-navigation">
      <div class="tab-container">
        <button
          @click="activeTab = 'basic'"
          class="tab-btn"
          :class="{ active: activeTab === 'basic' }"
        >
          {{ t('registration.basicInformation') }}
        </button>
        <button
          @click="activeTab = 'fields'"
          class="tab-btn"
          :class="{ active: activeTab === 'fields' }"
        >
          {{ t('registration.formFields') }}
        </button>

        <button
          @click="activeTab = 'translations'"
          class="tab-btn"
          :class="{ active: activeTab === 'translations' }"
        >
          {{ t('registration.formTranslations') }}
        </button>
        <button
          @click="activeTab = 'preview'"
          class="tab-btn"
          :class="{ active: activeTab === 'preview' }"
        >
          {{ t('registration.formPreview') }}
        </button>
        <button
          @click="activeTab = 'email'"
          class="tab-btn"
          :class="{ active: activeTab === 'email' }"
        >
          {{ t('registration.emailSettings') || 'Email' }}
        </button>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="form-builder-content">
      <!-- Basic Information Tab -->
      <div v-if="activeTab === 'basic'" class="tab-content">
        <div class="content-section basic-info-section">
          <h4>{{ t('registration.basicInformation') }}</h4>

          <div class="form-grid basic-info-grid">
            <div class="form-group">
              <label for="form-name">{{ t('registration.formName') }} *</label>
              <input
                id="form-name"
                v-model="formData.name"
                type="text"
                required
                :placeholder="t('registration.formNamePlaceholder')"
              />
            </div>

            <div class="form-group">
              <label for="form-title"
                >{{ t('registration.formTitle') }} *</label
              >
              <input
                id="form-title"
                v-model="formData.title"
                type="text"
                required
                :placeholder="t('registration.formTitlePlaceholder')"
              />
            </div>

            <div class="form-group full-width">
              <label for="form-description">{{
                t('registration.formDescription')
              }}</label>
              <textarea
                id="form-description"
                v-model="formData.description"
                rows="3"
                :placeholder="t('registration.formDescriptionPlaceholder')"
              ></textarea>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="formData.isActive" />
                {{ t('registration.formActive') }}
              </label>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="formData.settings.multiAthlete"
                />
                {{ t('registration.multiAthleteRegistration') }}
              </label>
              <p class="help-text">{{ t('registration.multiAthleteHelp') }}</p>
            </div>

            <!-- Team Manager Fields Builder moved to Fields tab -->
          </div>
        </div>

        <!-- Public Registrants List Settings -->
        <div class="content-section basic-public-section">
          <h4>
            {{
              t('registration.publicRegistrantsTitle') ||
              'Public Registrants List'
            }}
          </h4>
          <div class="form-grid basic-info-grid">
            <div class="form-group full-width">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="formData.settings.publicRegistrants.enabled"
                />
                {{
                  t('registration.publicRegistrantsEnable') ||
                  'Enable public registrants list'
                }}
              </label>
            </div>

            <template v-if="formData.settings.publicRegistrants.enabled">
              <div class="form-group full-width">
                <label>{{
                  t('registration.publicRegistrantsVisibleFields') ||
                  'Visible fields'
                }}</label>
                <div class="visible-fields">
                  <div
                    v-for="(key, idx) in publicVisibleKeys"
                    :key="key"
                    class="visible-field-row"
                  >
                    <span class="drag-index">{{ idx + 1 }}.</span>
                    <select v-model="publicVisibleKeys[idx]">
                      <option
                        v-for="opt in publicFieldOptions"
                        :key="opt.value"
                        :value="opt.value"
                        :disabled="isKeySelectedElsewhere(opt.value, idx)"
                      >
                        {{ opt.label }} ({{ opt.value }})
                      </option>
                    </select>
                    <input
                      class="label-override"
                      :placeholder="
                        t('registration.columnLabelOptional') ||
                        'Column label (optional)'
                      "
                      :value="publicColumnLabels[key] || ''"
                      @input="onColumnLabelChange(key, $event.target.value)"
                    />
                    <div class="row-actions">
                      <button
                        type="button"
                        class="btn btn-secondary"
                        @click="movePublicKey(idx, -1)"
                        :disabled="idx === 0"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        class="btn btn-secondary"
                        @click="movePublicKey(idx, 1)"
                        :disabled="idx === publicVisibleKeys.length - 1"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        class="btn btn-danger"
                        @click="removePublicKey(idx)"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div class="actions-row">
                    <button
                      type="button"
                      class="btn btn-primary"
                      @click="addPublicKey"
                    >
                      {{ t('button.add') }}
                    </button>
                  </div>
                </div>
                <p class="hint">
                  {{
                    t('registration.publicRegistrantsHint') ||
                    'Select which athlete fields to show publicly. Personal contact fields (email, phone) are not available.'
                  }}
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Fields Tab -->
      <div v-if="activeTab === 'fields'" class="tab-content fields-tab">
        <div class="fields-left-panel">
          <FieldsList
            :fields="formData.fields"
            :selected-index="selectedFieldIndex"
            @select="selectField"
            @move="moveField"
            @duplicate="duplicateField"
            @remove="removeField"
            @add="addField"
          />

          <!-- Team Manager Fields Builder (same field types as athletes) -->
          <div
            v-if="formData.settings.multiAthlete"
            class="form-group team-manager-settings"
          >
            <h5>{{ t('registration.teamManagerFields') }}</h5>
            <p class="help-text">
              {{ t('registration.teamManagerFieldsHelp') }}
            </p>

            <div class="team-manager-fields-builder">
              <div class="fields-header">
                <span>{{ t('registration.teamManagerFieldsList') }}</span>
                <button
                  @click="addTeamManagerField"
                  class="btn btn-primary btn-sm"
                >
                  <span class="btn-icon">➕</span>
                  {{ t('registration.addTeamManagerField') }}
                </button>
              </div>

              <div
                v-if="formData.settings.teamManagerFields.length === 0"
                class="empty-team-fields"
              >
                <p>{{ t('registration.noTeamManagerFields') }}</p>
                <button
                  @click="addTeamManagerField"
                  class="btn btn-primary btn-sm"
                >
                  {{ t('registration.addFirstTeamManagerField') }}
                </button>
              </div>

              <div v-else class="team-manager-fields-list">
                <div
                  v-for="(field, index) in formData.settings.teamManagerFields"
                  :key="field.id"
                  class="team-field-item"
                  :class="{ active: selectedTeamManagerFieldIndex === index }"
                  @click="selectTeamManagerField(index)"
                >
                  <div class="field-header">
                    <div class="field-info">
                      <span class="field-label">{{
                        field.label || t('registration.unnamedField')
                      }}</span>
                      <div class="field-meta">
                        <span class="field-type">{{
                          getFieldTypeLabel(field.type)
                        }}</span>
                        <span v-if="field.required" class="required-indicator"
                          >Required</span
                        >
                      </div>
                    </div>
                    <div class="field-actions">
                      <button
                        @click.stop="moveTeamManagerField(index, -1)"
                        :disabled="index === 0"
                        class="btn-icon"
                        title="Move Up"
                      >
                        ↑
                      </button>
                      <button
                        @click.stop="moveTeamManagerField(index, 1)"
                        :disabled="
                          index ===
                          formData.settings.teamManagerFields.length - 1
                        "
                        class="btn-icon"
                        title="Move Down"
                      >
                        ↓
                      </button>
                      <button
                        @click.stop="removeTeamManagerField(index)"
                        class="btn-icon btn-danger"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Removed inline Team Manager Field Editor; selection now edits on right panel -->
            </div>
          </div>
        </div>

        <div class="fields-right-panel">
          <FieldEditor
            v-if="editingTargetField"
            :field="editingTargetField"
            :manually-edited="isManuallyEdited"
            :duplicate-names="editorDuplicateNames"
            @typeChange="onEditorTypeChange"
            @sanitizeName="sanitizeEditorName"
            @enableManual="enableEditorManual"
            @disableManual="disableEditorManual"
            @addOption="addEditorOption"
            @removeOption="removeEditorOption"
          />
        </div>
        <div v-if="false && selectedField" class="field-editor-panel">
          <div class="panel-header">
            <h4>{{ t('registration.editField') }}</h4>
            <span class="field-type-badge">{{
              getFieldTypeLabel(selectedField.type)
            }}</span>
          </div>

          <div class="editor-content">
            <div class="form-group">
              <label>{{ t('registration.fieldType') }} *</label>
              <select
                v-model="selectedField.type"
                @change="onFieldTypeChange"
                class="field-type-select"
              >
                <option value="text">
                  {{ t('registration.fieldTypes.text') }}
                </option>
                <option value="email">
                  {{ t('registration.fieldTypes.email') }}
                </option>
                <option value="name">
                  {{ t('registration.fieldTypes.name') }}
                </option>
                <option value="number">
                  {{ t('registration.fieldTypes.number') }}
                </option>
                <option value="textarea">
                  {{ t('registration.fieldTypes.textarea') }}
                </option>
                <option value="select">
                  {{ t('registration.fieldTypes.select') }}
                </option>
                <option value="radio">
                  {{ t('registration.fieldTypes.radio') }}
                </option>
                <option value="checkbox">
                  {{ t('registration.fieldTypes.checkbox') }}
                </option>
                <option value="date">
                  {{ t('registration.fieldTypes.date') }}
                </option>
                <option value="time">
                  {{ t('registration.fieldTypes.time') }}
                </option>
                <option value="section">
                  {{ t('registration.fieldTypes.section') }}
                </option>
              </select>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>{{ t('registration.fieldLabel') }} *</label>
                <input
                  v-model="selectedField.label"
                  type="text"
                  required
                  :placeholder="t('registration.fieldLabelPlaceholder')"
                />
              </div>

              <div v-if="selectedField.type !== 'section'" class="form-group">
                <label>{{ t('registration.fieldName') }} *</label>
                <div class="field-name-container">
                  <input
                    v-model="selectedField.name"
                    type="text"
                    required
                    :placeholder="t('registration.fieldNamePlaceholder')"
                    :disabled="!manuallyEditedNames.has(selectedField.id)"
                    @input="sanitizeFieldName"
                    :class="{
                      error: duplicateFieldNames.includes(selectedField.name),
                      'auto-generated': !manuallyEditedNames.has(
                        selectedField.id
                      ),
                    }"
                  />
                  <button
                    v-if="!manuallyEditedNames.has(selectedField.id)"
                    @click="enableManualEdit"
                    class="btn btn-secondary btn-sm edit-name-btn"
                    type="button"
                    title="Enable manual editing of field name"
                  >
                    <span class="btn-icon">✏️</span>
                    Edit
                  </button>
                  <button
                    v-else
                    @click="disableManualEdit"
                    class="btn btn-outline btn-sm edit-name-btn"
                    type="button"
                    title="Reset to auto-generated field name"
                  >
                    <span class="btn-icon">🔄</span>
                    Auto
                  </button>
                </div>
                <small class="field-help">
                  <span v-if="!manuallyEditedNames.has(selectedField.id)">
                    Auto-generated from field label. Click "Edit" to customize.
                  </span>
                  <span v-else>
                    {{ t('registration.fieldNameHelp') }}
                  </span>
                </small>
                <small
                  v-if="duplicateFieldNames.includes(selectedField.name)"
                  class="field-error"
                >
                  ⚠️ This field name is used by another field. Field names must
                  be unique.
                </small>
              </div>
            </div>

            <div v-if="selectedField.type !== 'section'" class="form-group">
              <label>{{ t('registration.fieldPlaceholder') }}</label>
              <input
                v-model="selectedField.placeholder"
                type="text"
                :placeholder="t('registration.fieldPlaceholderPlaceholder')"
              />
            </div>

            <div v-if="selectedField.type === 'section'" class="form-group">
              <label>{{ t('registration.sectionDescription') }}</label>
              <textarea
                v-model="selectedField.description"
                rows="3"
                :placeholder="t('registration.sectionDescriptionPlaceholder')"
              ></textarea>
            </div>

            <div v-if="selectedField.type !== 'section'" class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="selectedField.required" />
                {{ t('registration.fieldRequired') }}
              </label>
            </div>

            <!-- Options for select, radio, checkbox -->
            <div v-if="hasOptions(selectedField.type)" class="form-group">
              <label>{{ t('registration.fieldOptions') }}</label>
              <div class="options-container">
                <div
                  v-for="(option, optionIndex) in selectedField.options"
                  :key="optionIndex"
                  class="option-item"
                >
                  <input
                    v-model="selectedField.options[optionIndex]"
                    type="text"
                    :placeholder="`Option ${optionIndex + 1}`"
                  />
                  <button
                    @click="removeOption(optionIndex)"
                    class="btn-icon btn-danger"
                  >
                    ✕
                  </button>
                </div>
                <button
                  @click="addOption"
                  class="btn btn-secondary btn-sm add-option-btn"
                >
                  <span class="btn-icon">➕</span>
                  {{ t('registration.addOption') }}
                </button>
              </div>
            </div>

            <!-- Validation rules -->
            <div v-if="selectedField.type !== 'section'" class="form-group">
              <label>{{ t('registration.validation') }}</label>
              <div class="validation-grid">
                <div
                  v-if="
                    selectedField.type === 'text' ||
                    selectedField.type === 'textarea'
                  "
                  class="validation-item"
                >
                  <label>{{ t('registration.minLength') }}</label>
                  <input
                    v-model.number="selectedField.validation.minLength"
                    type="number"
                    min="0"
                    placeholder="Min"
                  />
                </div>
                <div
                  v-if="
                    selectedField.type === 'text' ||
                    selectedField.type === 'textarea'
                  "
                  class="validation-item"
                >
                  <label>{{ t('registration.maxLength') }}</label>
                  <input
                    v-model.number="selectedField.validation.maxLength"
                    type="number"
                    min="0"
                    placeholder="Max"
                  />
                </div>
                <div
                  v-if="selectedField.type === 'number'"
                  class="validation-item"
                >
                  <label>{{ t('registration.minValue') }}</label>
                  <input
                    v-model.number="selectedField.validation.min"
                    type="number"
                    placeholder="Min"
                  />
                </div>
                <div
                  v-if="selectedField.type === 'number'"
                  class="validation-item"
                >
                  <label>{{ t('registration.maxValue') }}</label>
                  <input
                    v-model.number="selectedField.validation.max"
                    type="number"
                    placeholder="Max"
                  />
                </div>
              </div>
            </div>

            <!-- Field Preview -->
            <div class="form-group">
              <label>Field Preview</label>
              <div class="field-preview">
                <label class="preview-label">
                  {{ selectedField.label || 'Field Label' }}
                  <span v-if="selectedField.required" class="required">*</span>
                </label>
                <div class="preview-input">
                  <input
                    v-if="
                      [
                        'text',
                        'email',
                        'tel',
                        'number',
                        'date',
                        'time',
                        'url',
                        'password',
                      ].includes(selectedField.type)
                    "
                    :type="selectedField.type"
                    :placeholder="selectedField.placeholder"
                    disabled
                  />
                  <textarea
                    v-else-if="selectedField.type === 'textarea'"
                    :placeholder="selectedField.placeholder"
                    rows="3"
                    disabled
                  ></textarea>
                  <select v-else-if="selectedField.type === 'select'" disabled>
                    <option>
                      {{
                        selectedField.placeholder ||
                        t('registration.selectOption')
                      }}
                    </option>
                    <option
                      v-for="option in selectedField.options"
                      :key="option"
                    >
                      {{ option }}
                    </option>
                  </select>
                  <div
                    v-else-if="selectedField.type === 'radio'"
                    class="radio-preview"
                  >
                    <div
                      v-if="
                        !selectedField.options ||
                        selectedField.options.length === 0
                      "
                      class="no-options-message"
                    >
                      No options added yet. Add options in the field settings
                      above.
                    </div>
                    <div
                      v-for="option in selectedField.options"
                      :key="option"
                      class="radio-option"
                    >
                      <input
                        type="radio"
                        :name="'preview-' + selectedField.name"
                        disabled
                      />
                      <label>{{ option }}</label>
                    </div>
                  </div>
                  <div
                    v-else-if="selectedField.type === 'checkbox'"
                    class="checkbox-preview"
                  >
                    <div
                      v-if="
                        !selectedField.options ||
                        selectedField.options.length === 0
                      "
                      class="no-options-message"
                    >
                      No options added yet. Add options in the field settings
                      above.
                    </div>
                    <div
                      v-for="option in selectedField.options"
                      :key="option"
                      class="checkbox-option"
                    >
                      <input type="checkbox" disabled />
                      <label>{{ option }}</label>
                    </div>
                  </div>
                  <input
                    v-else-if="selectedField.type === 'file'"
                    type="file"
                    disabled
                  />
                  <div
                    v-else-if="selectedField.type === 'section'"
                    class="section-preview"
                  >
                    <div class="section-divider"></div>
                    <h4 class="section-title">
                      {{ selectedField.label || 'Section Title' }}
                    </h4>
                    <p
                      v-if="selectedField.description"
                      class="section-description"
                    >
                      {{ selectedField.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Translations Tab -->
      <div v-if="activeTab === 'translations'" class="tab-content">
        <div class="content-section basic-info-section translations-section">
          <h4>{{ t('registration.formTranslations') }}</h4>

          <div class="form-grid basic-info-grid translations-grid">
            <div class="translation-group">
              <h5>General & Buttons</h5>
              <div class="form-group">
                <label>{{ t('registration.athleteTitle') }}</label>
                <input
                  v-model="formData.translations.athleteTitle"
                  type="text"
                  :placeholder="t('registration.athleteTitlePlaceholder')"
                />
              </div>
              <div class="form-group">
                <label>{{ t('registration.submitButtonText') }}</label>
                <input
                  v-model="formData.translations.submitButton"
                  type="text"
                  :placeholder="t('registration.submitButtonPlaceholder')"
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.resetButtonText') }}</label>
                <input
                  v-model="formData.translations.resetButton"
                  type="text"
                  :placeholder="t('registration.resetButtonPlaceholder')"
                />
              </div>
            </div>

            <!-- Date translations removed from form-level; now per-field in FieldEditor -->

            <div class="translation-group">
              <h5>Messages</h5>
              <div class="form-group">
                <label>{{ t('registration.requiredFieldMessage') }}</label>
                <input
                  v-model="formData.translations.requiredFieldMessage"
                  type="text"
                  :placeholder="
                    t('registration.requiredFieldMessagePlaceholder')
                  "
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.successMessage') }}</label>
                <textarea
                  v-model="formData.translations.successMessage"
                  rows="2"
                  :placeholder="t('registration.successMessagePlaceholder')"
                ></textarea>
              </div>

              <div class="form-group">
                <label>{{ t('registration.errorMessage') }}</label>
                <textarea
                  v-model="formData.translations.errorMessage"
                  rows="2"
                  :placeholder="t('registration.errorMessagePlaceholder')"
                ></textarea>
              </div>
            </div>

            <div class="translation-group">
              <h5>Competition Flow</h5>
              <div class="form-group">
                <label>{{ t('registration.selectCompetitionLabel') }}</label>
                <input
                  v-model="formData.translations.selectCompetitionLabel"
                  type="text"
                  :placeholder="
                    t('registration.selectCompetitionLabelPlaceholder')
                  "
                />
              </div>

              <div class="form-group">
                <label>{{
                  t('registration.selectCompetitionPlaceholder')
                }}</label>
                <input
                  v-model="formData.translations.selectCompetitionPlaceholder"
                  type="text"
                  :placeholder="
                    t('registration.selectCompetitionPlaceholderText')
                  "
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.selectCompetitionError') }}</label>
                <input
                  v-model="formData.translations.selectCompetitionError"
                  type="text"
                  :placeholder="
                    t('registration.selectCompetitionErrorPlaceholder')
                  "
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.registrationClosed') }}</label>
                <input
                  v-model="formData.translations.registrationClosed"
                  type="text"
                  :placeholder="t('registration.registrationClosedPlaceholder')"
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.competitionFull') }}</label>
                <input
                  v-model="formData.translations.competitionFull"
                  type="text"
                  :placeholder="t('registration.competitionFullPlaceholder')"
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.spotsLeft') }}</label>
                <input
                  v-model="formData.translations.spotsLeft"
                  type="text"
                  :placeholder="t('registration.spotsLeftPlaceholder')"
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.deadline') }}</label>
                <input
                  v-model="formData.translations.deadline"
                  type="text"
                  :placeholder="t('registration.deadlinePlaceholder')"
                />
              </div>
            </div>

            <div class="translation-group">
              <h5>Public Registrations</h5>
              <div class="form-group">
                <label>Registrations Header</label>
                <input
                  v-model="formData.translations.publicRegistrantsTitle"
                  type="text"
                  placeholder="Registered Athletes"
                />
              </div>
              <div class="form-group">
                <label>Register Button</label>
                <input
                  v-model="formData.translations.toggleRegister"
                  type="text"
                  placeholder="Register"
                />
              </div>
              <div class="form-group">
                <label>Registrations Button</label>
                <input
                  v-model="formData.translations.toggleRegistrations"
                  type="text"
                  placeholder="Registrations"
                />
              </div>
              <div class="form-group">
                <label>Refresh Button</label>
                <input
                  v-model="formData.translations.refreshButton"
                  type="text"
                  placeholder="Refresh"
                />
              </div>
            </div>

            <!-- Multi-Athlete Translations (only show if multi-athlete is enabled) -->
            <div
              v-if="formData.settings.multiAthlete"
              class="translation-group"
            >
              <h5>{{ t('registration.multiAthleteTranslations') }}</h5>

              <div class="form-group">
                <label>{{ t('registration.addAthleteButton') }}</label>
                <input
                  v-model="formData.translations.addAthleteButton"
                  type="text"
                  :placeholder="t('registration.addAthleteButtonPlaceholder')"
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.removeAthleteButton') }}</label>
                <input
                  v-model="formData.translations.removeAthleteButton"
                  type="text"
                  :placeholder="
                    t('registration.removeAthleteButtonPlaceholder')
                  "
                />
              </div>

              <div class="form-group">
                <label>{{ t('registration.teamManagerContactTitle') }}</label>
                <input
                  v-model="formData.translations.teamManagerContactTitle"
                  type="text"
                  :placeholder="
                    t('registration.teamManagerContactTitlePlaceholder')
                  "
                />
              </div>

              <div class="form-group">
                <label>{{
                  t('registration.teamManagerSectionDescription')
                }}</label>
                <textarea
                  v-model="formData.translations.teamManagerSectionDescription"
                  rows="2"
                  :placeholder="
                    t('registration.teamManagerSectionDescriptionPlaceholder')
                  "
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Email Tab -->
      <div v-if="activeTab === 'email'" class="tab-content">
        <div class="content-section basic-info-section email-settings-section">
          <h4>
            {{ t('registration.emailSettings') || 'Email Notifications' }}
          </h4>

          <div class="form-grid email-settings-grid">
            <div class="form-group full-width">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="formData.settings.email.enabled"
                />
                {{
                  t('registration.enableEmailOnSubmit') ||
                  'Send confirmation email on submit'
                }}
              </label>
            </div>

            <div class="form-group full-width smtp-settings-card">
              <h5 class="email-subsection-title">
                {{ t('registration.smtpSettingsTitle') || 'SMTP Settings' }}
              </h5>
              <div class="smtp-compact-grid">
                <div class="form-group">
                  <label>{{ t('registration.smtpHost') || 'SMTP Host' }}</label>
                  <input
                    v-model="formData.settings.email.smtp.host"
                    type="text"
                    :disabled="!formData.settings.email.enabled"
                    placeholder="smtp.example.com"
                  />
                </div>

                <div class="form-group">
                  <label>{{ t('registration.smtpPort') || 'SMTP Port' }}</label>
                  <input
                    v-model.number="formData.settings.email.smtp.port"
                    type="number"
                    min="1"
                    :disabled="!formData.settings.email.enabled"
                    placeholder="587"
                  />
                </div>

                <div class="form-group">
                  <label>{{ t('registration.smtpUser') || 'SMTP User' }}</label>
                  <input
                    v-model="formData.settings.email.smtp.user"
                    type="text"
                    :disabled="!formData.settings.email.enabled"
                    placeholder="no-reply@example.com"
                  />
                </div>

                <div class="form-group">
                  <label>{{
                    t('registration.smtpPass') || 'SMTP Password'
                  }}</label>
                  <input
                    v-model="formData.settings.email.smtp.pass"
                    type="password"
                    :disabled="!formData.settings.email.enabled"
                    placeholder="••••••••"
                    autocomplete="new-password"
                  />
                </div>

                <div class="form-group full-span">
                  <label class="checkbox-label compact-checkbox-label">
                    <input
                      type="checkbox"
                      v-model="formData.settings.email.smtp.secure"
                      :disabled="!formData.settings.email.enabled"
                    />
                    {{ t('registration.smtpSecure') || 'Use SSL/TLS (secure)' }}
                  </label>
                </div>
              </div>
            </div>

            <!-- Test Connection Button -->
            <div class="form-group full-width">
              <button
                @click="testEmailConnection"
                :disabled="
                  !formData.settings.email.enabled ||
                  !isEmailConfigValid ||
                  isTestingConnection
                "
                class="btn btn-secondary"
                type="button"
              >
                <span v-if="isTestingConnection" class="btn-icon">⏳</span>
                <span v-else class="btn-icon">🔗</span>
                {{
                  isTestingConnection
                    ? t('registration.testingConnection') || 'Testing...'
                    : t('registration.testConnection') || 'Test Connection'
                }}
              </button>
              <p class="help-text">
                {{
                  t('registration.testConnectionHelp') ||
                  'Test your SMTP settings before saving. This will attempt to connect to your mail server.'
                }}
              </p>
              <div
                v-if="connectionTestResult"
                class="connection-result"
                :class="
                  connectionTestResult.success
                    ? 'connection-success'
                    : 'connection-error'
                "
              >
                <strong class="connection-result-title">
                  {{
                    connectionTestResult.success
                      ? t('general.success') || 'Success'
                      : t('general.error') || 'Error'
                  }}
                </strong>
                <span class="connection-result-message">
                  {{ connectionTestResult.message }}
                </span>
              </div>
            </div>

            <div class="form-group full-width sender-settings-card">
              <h5 class="email-subsection-title">
                {{ t('registration.senderSettingsTitle') || 'Sender Settings' }}
              </h5>
              <div class="sender-compact-grid">
                <div class="form-group">
                  <label>{{
                    t('registration.fromEmail') || 'From Email'
                  }}</label>
                  <input
                    v-model="formData.settings.email.fromEmail"
                    type="email"
                    :disabled="!formData.settings.email.enabled"
                    placeholder="no-reply@example.com"
                  />
                </div>

                <div class="form-group">
                  <label>{{ t('registration.fromName') || 'From Name' }}</label>
                  <input
                    v-model="formData.settings.email.fromName"
                    type="text"
                    :disabled="!formData.settings.email.enabled"
                    :placeholder="formData.title || 'Registration'"
                  />
                </div>
              </div>
            </div>

            <div
              class="form-group full-width athlete-template-card"
              v-if="emailFieldOptions.length > 0"
            >
              <h5 class="email-subsection-title">
                {{
                  t('registration.athleteEmailTemplateTitle') ||
                  'Athlete Email Template'
                }}
              </h5>

              <div class="form-group">
                <label>{{
                  t('registration.recipientField') || 'Recipient field'
                }}</label>
                <select
                  v-model="formData.settings.email.recipientField"
                  :disabled="!formData.settings.email.enabled"
                >
                  <option
                    v-for="opt in emailFieldOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }} ({{ opt.value }})
                  </option>
                </select>
                <p class="help-text" v-if="emailFieldOptions.length > 1">
                  {{
                    t('registration.recipientFieldHint') ||
                    'Choose which email field the confirmation will be sent to.'
                  }}
                </p>
              </div>

              <div class="form-group">
                <label>{{
                  t('registration.emailSubject') || 'Email Subject'
                }}</label>
                <input
                  v-model="formData.settings.email.subject"
                  type="text"
                  :disabled="!formData.settings.email.enabled"
                  :placeholder="
                    t('registration.emailSubjectPlaceholder') ||
                    'Registration Confirmation'
                  "
                />
                <p class="help-text">
                  {{
                    t('registration.emailSubjectHelp') ||
                    'Subject line for the confirmation email sent to participants'
                  }}
                </p>
              </div>

              <div class="form-group">
                <label>{{
                  t('registration.emailMessage') || 'Email Message'
                }}</label>
                <textarea
                  v-model="formData.settings.email.message"
                  :disabled="!formData.settings.email.enabled"
                  :placeholder="
                    t('registration.emailMessagePlaceholder') ||
                    'Thank you for your registration...'
                  "
                  rows="6"
                  class="form-textarea"
                ></textarea>
                <p class="help-text">
                  {{
                    t('registration.emailMessageHelp') ||
                    'Custom message to send to participants. You can use placeholders:'
                  }}
                </p>

                <div class="placeholder-legend">
                  <h5>
                    {{
                      t('registration.availablePlaceholders') ||
                      'Available Placeholders'
                    }}
                  </h5>
                  <div class="placeholder-grid">
                    <div class="placeholder-item">
                      <code>{participantName}</code>
                      <span>{{
                        t('registration.placeholderParticipantName') ||
                        "Participant's name"
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{competitionName}</code>
                      <span>{{
                        t('registration.placeholderCompetitionName') ||
                        'Competition name'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{cupName}</code>
                      <span>{{
                        t('registration.placeholderCupName') ||
                        'Cup/Season name'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{formTitle}</code>
                      <span>{{
                        t('registration.placeholderFormTitle') || 'Form title'
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="form-group full-width manager-template-card"
              v-if="
                formData.settings.multiAthlete &&
                managerEmailFieldOptions.length > 0
              "
            >
              <h5 class="email-subsection-title">
                {{
                  t('registration.managerEmailTemplateTitle') ||
                  'Manager Email Template'
                }}
              </h5>

              <div class="form-group">
                <label>{{
                  t('registration.managerRecipientField') ||
                  'Manager recipient field'
                }}</label>
                <select
                  v-model="formData.settings.email.managerRecipientField"
                  :disabled="!formData.settings.email.enabled"
                >
                  <option value="">
                    {{ t('registration.noneOption') || 'None' }}
                  </option>
                  <option
                    v-for="opt in managerEmailFieldOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }} ({{ opt.value }})
                  </option>
                </select>
                <p class="help-text">
                  {{
                    t('registration.managerRecipientFieldHint') ||
                    'Choose the team manager email field for the summary email.'
                  }}
                </p>
              </div>

              <div class="form-group">
                <label>{{
                  t('registration.managerEmailSubject') ||
                  'Manager Email Subject'
                }}</label>
                <input
                  v-model="formData.settings.email.managerSubject"
                  type="text"
                  :disabled="!formData.settings.email.enabled"
                  :placeholder="
                    t('registration.managerEmailSubjectPlaceholder') ||
                    'Registration Summary'
                  "
                />
              </div>

              <div class="form-group">
                <label>{{
                  t('registration.managerEmailMessage') ||
                  'Manager Email Message'
                }}</label>
                <textarea
                  v-model="formData.settings.email.managerMessage"
                  :disabled="!formData.settings.email.enabled"
                  :placeholder="
                    t('registration.managerEmailMessagePlaceholder') ||
                    'Summary for {participantName}...'
                  "
                  rows="8"
                  class="form-textarea"
                ></textarea>
                <p class="help-text">
                  {{
                    t('registration.managerEmailMessageHelp') ||
                    'Custom summary message for team managers. You can use placeholders:'
                  }}
                </p>
                <div class="placeholder-legend">
                  <h5>
                    {{
                      t('registration.availablePlaceholders') ||
                      'Available Placeholders'
                    }}
                  </h5>
                  <div class="placeholder-grid">
                    <div class="placeholder-item">
                      <code>{participantName}</code>
                      <span>{{
                        t('registration.placeholderParticipantNameList') ||
                        'All participant names (comma-separated for multi-athlete)'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{competitionName}</code>
                      <span>{{
                        t('registration.placeholderCompetitionName') ||
                        'Competition name'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{cupName}</code>
                      <span>{{
                        t('registration.placeholderCupName') ||
                        'Cup/Season name'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{formTitle}</code>
                      <span>{{
                        t('registration.placeholderFormTitle') || 'Form title'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{managerName}</code>
                      <span>{{
                        t('registration.placeholderManagerName') ||
                        "Manager's name"
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{registrationsText}</code>
                      <span>{{
                        t('registration.placeholderRegistrationsText') ||
                        'Formatted registration summary text'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{registrationsJson}</code>
                      <span>{{
                        t('registration.placeholderRegistrationsJson') ||
                        'Raw registrations data as JSON'
                      }}</span>
                    </div>
                    <div class="placeholder-item">
                      <code>{athleteCount}</code>
                      <span>{{
                        t('registration.placeholderAthleteCount') ||
                        'Number of athletes in the submission'
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Preview Tab -->
      <div v-if="activeTab === 'preview'" class="tab-content">
        <div class="preview-container content-section basic-info-section">
          <h4>{{ t('registration.formPreview') }}</h4>
          <div class="form-preview">
            <div class="preview-header">
              <h3>{{ formData.title || t('registration.untitledForm') }}</h3>
              <p v-if="formData.description">{{ formData.description }}</p>
            </div>

            <div class="preview-fields">
              <div
                v-if="formData.fields.length === 0"
                class="no-fields-preview"
              >
                <p>
                  No fields added yet. Go to the Fields tab to add form fields.
                </p>
              </div>
              <div
                v-for="field in formData.fields"
                :key="field.id"
                class="preview-field"
              >
                <label class="preview-label">
                  {{ field.label }}
                  <span v-if="field.required" class="required">*</span>
                </label>
                <div class="preview-input">
                  <input
                    v-if="
                      [
                        'text',
                        'email',
                        'tel',
                        'number',
                        'date',
                        'time',
                        'url',
                        'password',
                      ].includes(field.type)
                    "
                    :type="field.type"
                    :placeholder="field.placeholder"
                    disabled
                  />
                  <textarea
                    v-else-if="field.type === 'textarea'"
                    :placeholder="field.placeholder"
                    rows="3"
                    disabled
                  ></textarea>
                  <select v-else-if="field.type === 'select'" disabled>
                    <option>
                      {{ field.placeholder || t('registration.selectOption') }}
                    </option>
                    <option v-for="option in field.options" :key="option">
                      {{ option }}
                    </option>
                  </select>
                  <div v-else-if="field.type === 'radio'" class="radio-preview">
                    <div
                      v-if="!field.options || field.options.length === 0"
                      class="no-options-message"
                    >
                      No options configured for this field.
                    </div>
                    <div
                      v-for="option in field.options"
                      :key="option"
                      class="radio-option"
                    >
                      <input
                        type="radio"
                        :name="'preview-' + field.name"
                        disabled
                      />
                      <label>{{ option }}</label>
                    </div>
                  </div>
                  <div
                    v-else-if="field.type === 'checkbox'"
                    class="checkbox-preview"
                  >
                    <div
                      v-if="!field.options || field.options.length === 0"
                      class="no-options-message"
                    >
                      No options added yet. Add options in the field settings
                      above.
                    </div>
                    <div
                      v-for="option in field.options"
                      :key="option"
                      class="checkbox-option"
                    >
                      <input type="checkbox" disabled />
                      <label>{{ option }}</label>
                    </div>
                  </div>
                  <input
                    v-else-if="field.type === 'file'"
                    type="file"
                    disabled
                  />
                  <div
                    v-else-if="field.type === 'section'"
                    class="section-preview"
                  >
                    <div class="section-divider"></div>
                    <h4 class="section-title">
                      {{ field.label || 'Section Title' }}
                    </h4>
                    <p v-if="field.description" class="section-description">
                      {{ field.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="preview-actions">
              <button class="preview-submit-btn" disabled>
                {{ formData.translations.submitButton || 'Submit' }}
              </button>
              <button class="preview-reset-btn" disabled>
                {{ formData.translations.resetButton || 'Reset' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="floating-buttons">
      <button
        @click="saveForm"
        :disabled="!isFormValid"
        class="btn btn-primary"
      >
        {{ t('button.save') }}
      </button>
      <button @click="$emit('close')" class="btn btn-cancel">
        {{ t('button.cancel') }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import FieldsList from './FieldsList.vue'
import FieldEditor from './FieldEditor.vue'

export default {
  name: 'FormBuilder',
  components: {
    FieldsList,
    FieldEditor,
  },
  props: {
    form: {
      type: Object,
      default: null,
    },
  },
  emits: ['close', 'save'],
  setup(props, { emit }) {
    const { t } = useI18n()
    // Public registrants helpers
    const publicVisibleKeys = computed({
      get: () => formData.value.settings.publicRegistrants.visibleFieldKeys,
      set: (v) =>
        (formData.value.settings.publicRegistrants.visibleFieldKeys = v),
    })
    const publicColumnLabels = computed({
      get: () => formData.value.settings.publicRegistrants.columnLabels || {},
      set: (v) =>
        (formData.value.settings.publicRegistrants.columnLabels = v || {}),
    })
    const publicFieldOptions = computed(() => {
      // Athlete fields only; exclude obvious PII types
      const disallowTypes = new Set(['email', 'tel', 'phone'])
      return (formData.value.fields || [])
        .filter(
          (f) =>
            f &&
            f.name &&
            f.type &&
            !disallowTypes.has(String(f.type).toLowerCase())
        )
        .map((f) => ({ value: f.name, label: f.label || f.name }))
    })
    const isKeySelectedElsewhere = (key, exceptIdx) => {
      return publicVisibleKeys.value.some(
        (k, i) => k === key && i !== exceptIdx
      )
    }
    const addPublicKey = () => {
      const first = publicFieldOptions.value.find(
        (opt) => !publicVisibleKeys.value.includes(opt.value)
      )
      if (first)
        publicVisibleKeys.value = [...publicVisibleKeys.value, first.value]
    }
    const removePublicKey = (idx) => {
      const next = [...publicVisibleKeys.value]
      const removed = next.splice(idx, 1)[0]
      publicVisibleKeys.value = next
      if (removed && publicColumnLabels.value[removed]) {
        const labels = { ...publicColumnLabels.value }
        delete labels[removed]
        publicColumnLabels.value = labels
      }
    }
    const movePublicKey = (idx, dir) => {
      const next = [...publicVisibleKeys.value]
      const ni = idx + dir
      if (ni < 0 || ni >= next.length) return
      const [item] = next.splice(idx, 1)
      next.splice(ni, 0, item)
      publicVisibleKeys.value = next
    }
    const onColumnLabelChange = (key, value) => {
      const labels = { ...publicColumnLabels.value }
      if (value && value.trim()) labels[key] = value
      else delete labels[key]
      publicColumnLabels.value = labels
    }

    // Active tab
    const activeTab = ref('basic')

    // Form data

    const formData = ref({
      name: '',
      title: '',
      description: '',
      isActive: true,
      fields: [],
      settings: {
        multiAthlete: false,
        teamManagerFields: [],
        email: {
          enabled: false,
          smtp: { host: '', port: 587, secure: false, user: '', pass: '' },
          fromEmail: '',
          fromName: '',
          recipientField: '',
          managerRecipientField: '',
          subject: 'Registration Confirmation',
          message:
            'Thank you for your registration! Your submission has been received and is being processed.',
          managerSubject: 'Registration Summary',
          managerMessage: '',
        },
        publicRegistrants: {
          enabled: false,
          visibleFieldKeys: [],
          columnLabels: {},
        },
      },
      translations: {
        submitButton: 'Submit',
        resetButton: 'Reset',
        requiredFieldMessage: 'This field is required',
        successMessage: 'Form submitted successfully!',
        errorMessage: 'Please check your entries and try again.',
        selectCompetitionLabel: 'Select Competition',
        selectCompetitionPlaceholder: 'Please select a competition...',
        selectCompetitionError: 'Please select a competition',
        registrationClosed: 'Registration Closed',
        competitionFull: 'Full',
        spotsLeft: 'spots left',
        deadline: 'Deadline',
        athleteTitle: 'Athlete',
        addAthleteButton: 'Add Athlete',
        removeAthleteButton: 'Remove',
        teamManagerContactTitle: 'Team Manager Contact',
        teamManagerSectionDescription: '',
        // Public registrants UI (embed)
        publicRegistrantsTitle: 'Registered Athletes',
        toggleRegister: 'Register',
        toggleRegistrations: 'Registrations',
        refreshButton: 'Refresh',
      },
    })

    // Field management
    const selectedFieldIndex = ref(-1)
    const fieldIdCounter = ref(1)
    // Email recipient options from form fields
    const emailFieldOptions = computed(() => {
      return (formData.value.fields || [])
        .filter((f) => f && f.type === 'email' && f.name)
        .map((f) => ({ value: f.name, label: f.label || f.name }))
    })
    const managerEmailFieldOptions = computed(() => {
      return (formData.value.settings.teamManagerFields || [])
        .filter((f) => f && f.type === 'email' && f.name)
        .map((f) => ({ value: f.name, label: f.label || f.name }))
    })

    // Check if email configuration is valid
    const isEmailConfigValid = computed(() => {
      if (!formData.value.settings.email.enabled) return false
      const email = formData.value.settings.email
      return (
        email.smtp.host &&
        email.smtp.port &&
        email.smtp.user &&
        email.smtp.pass &&
        email.fromEmail &&
        (email.recipientField ||
          (formData.value.settings.multiAthlete && email.managerRecipientField))
      )
    })

    // Test connection state
    const isTestingConnection = ref(false)
    const connectionTestResult = ref(null)

    // Test email connection
    const testEmailConnection = async () => {
      if (!isEmailConfigValid.value) return

      isTestingConnection.value = true
      connectionTestResult.value = null

      try {
        const response = await axios.post('/registration/test-email', {
          smtp: formData.value.settings.email.smtp,
          fromEmail: formData.value.settings.email.fromEmail,
          fromName: formData.value.settings.email.fromName,
        })

        const result = response.data

        if (response.status === 200) {
          connectionTestResult.value = {
            success: true,
            message:
              'Connection successful! SMTP settings are working correctly.',
          }
        } else {
          connectionTestResult.value = {
            success: false,
            message:
              result.error ||
              'Connection failed. Please check your SMTP settings.',
          }
        }
      } catch (error) {
        console.error('Test connection error:', error)
        connectionTestResult.value = {
          success: false,
          message:
            'Connection test failed. Please check your network and try again.',
        }
      } finally {
        isTestingConnection.value = false
      }
    }

    // Keep recipientField valid as fields change
    watch(
      () => formData.value.fields,
      () => {
        const opts = emailFieldOptions.value
        const current = formData.value.settings.email.recipientField
        if (!opts.find((o) => o.value === current)) {
          formData.value.settings.email.recipientField = opts[0]
            ? opts[0].value
            : ''
        }
      },
      { deep: true, immediate: true }
    )
    watch(
      () => formData.value.settings.teamManagerFields,
      () => {
        const opts = managerEmailFieldOptions.value
        const current = formData.value.settings.email.managerRecipientField
        if (!opts.find((o) => o.value === current)) {
          formData.value.settings.email.managerRecipientField = opts[0]
            ? opts[0].value
            : ''
        }
      },
      { deep: true, immediate: true }
    )

    // Team Manager Field management
    const selectedTeamManagerFieldIndex = ref(-1)
    const teamManagerFieldIdCounter = ref(1)

    // When editing Team Manager fields in the same FieldEditor on the right,
    // we expose a computed target field that resolves to either the selected
    // athlete field or the selected team manager field.
    const editingTargetField = computed(() => {
      if (selectedTeamManagerFieldIndex.value >= 0) {
        return (
          formData.value.settings.teamManagerFields[
            selectedTeamManagerFieldIndex.value
          ] || null
        )
      }
      return selectedField.value
    })

    // Plumb editor bindings depending on which target is selected
    const isEditingTeamManager = computed(
      () => selectedTeamManagerFieldIndex.value >= 0
    )
    const isManuallyEdited = computed(() => {
      if (isEditingTeamManager.value) return false
      return (
        !!selectedField.value &&
        manuallyEditedNames.value.has(selectedField.value.id)
      )
    })
    const editorDuplicateNames = computed(() => {
      // Duplicate names validation applies only to athlete fields list
      return duplicateFieldNames.value
    })
    const onEditorTypeChange = () => {
      if (isEditingTeamManager.value) {
        onTeamManagerFieldTypeChange()
      } else {
        onFieldTypeChange()
      }
    }
    const sanitizeEditorName = () => {
      if (isEditingTeamManager.value) {
        sanitizeTeamManagerFieldName()
      } else {
        sanitizeFieldName()
      }
    }
    const enableEditorManual = () => {
      if (!isEditingTeamManager.value) enableManualEdit()
    }
    const disableEditorManual = () => {
      if (!isEditingTeamManager.value) disableManualEdit()
    }
    const addEditorOption = () => {
      if (isEditingTeamManager.value) addTeamManagerFieldOption()
      else addOption()
    }
    const removeEditorOption = (idx) => {
      if (isEditingTeamManager.value) removeTeamManagerFieldOption(idx)
      else removeOption(idx)
    }

    // Computed properties
    const isEditing = computed(() => !!props.form)
    const selectedField = computed(() => {
      return selectedFieldIndex.value >= 0
        ? formData.value.fields[selectedFieldIndex.value]
        : null
    })

    const selectedTeamManagerField = computed(() => {
      return selectedTeamManagerFieldIndex.value >= 0
        ? formData.value.settings.teamManagerFields[
            selectedTeamManagerFieldIndex.value
          ]
        : null
    })

    // Check for duplicate field names
    const duplicateFieldNames = computed(() => {
      const names = formData.value.fields
        .map((field) => field.name)
        .filter((name) => name && name.trim() !== '')

      const duplicates = names.filter(
        (name, index) => names.indexOf(name) !== index
      )
      return [...new Set(duplicates)]
    })

    const isFormValid = computed(() => {
      return (
        formData.value.name.trim() &&
        formData.value.title.trim() &&
        formData.value.fields.length > 0 &&
        formData.value.fields.every((field) => {
          // Section fields only need a label and type
          if (field.type === 'section') {
            return field.label && field.type
          }
          // Other fields need label, name, and type
          return field.label && field.name && field.type
        }) &&
        duplicateFieldNames.value.length === 0
      )
    })

    // Initialize form data
    const deepClone = (v) => {
      try {
        return JSON.parse(JSON.stringify(v))
      } catch {
        return v
      }
    }

    const initializeForm = () => {
      if (props.form) {
        formData.value = {
          ...formData.value, // Keep defaults
          ...deepClone(props.form),
          // Ensure isActive is properly converted to boolean
          isActive: Boolean(props.form.isActive),
          fields: Array.isArray(props.form.fields)
            ? deepClone(props.form.fields)
            : [],

          settings: {
            ...formData.value.settings,
            ...(props.form.settings || {}),
            teamManagerFields: Array.isArray(
              props.form.settings && props.form.settings.teamManagerFields
            )
              ? deepClone(props.form.settings.teamManagerFields)
              : Array.isArray(props.form.teamManagerFields)
              ? deepClone(props.form.teamManagerFields)
              : [],
            email: {
              enabled: !!(
                props.form.settings &&
                props.form.settings.email &&
                props.form.settings.email.enabled
              ),
              smtp: {
                host:
                  (props.form.settings &&
                    props.form.settings.email &&
                    props.form.settings.email.smtp &&
                    props.form.settings.email.smtp.host) ||
                  '',
                port:
                  (props.form.settings &&
                    props.form.settings.email &&
                    props.form.settings.email.smtp &&
                    props.form.settings.email.smtp.port) ||
                  587,
                secure: !!(
                  props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.smtp &&
                  props.form.settings.email.smtp.secure
                ),
                user:
                  (props.form.settings &&
                    props.form.settings.email &&
                    props.form.settings.email.smtp &&
                    props.form.settings.email.smtp.user) ||
                  '',
                pass:
                  (props.form.settings &&
                    props.form.settings.email &&
                    props.form.settings.email.smtp &&
                    props.form.settings.email.smtp.pass) ||
                  '',
              },
              fromEmail:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.fromEmail) ||
                '',
              fromName:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.fromName) ||
                '',
              recipientField:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.recipientField) ||
                '',
              managerRecipientField:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.managerRecipientField) ||
                '',
              subject:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.subject) ||
                'Registration Confirmation',
              message:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.message) ||
                'Thank you for your registration! Your submission has been received and is being processed.',
              managerSubject:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.managerSubject) ||
                'Registration Summary',
              managerMessage:
                (props.form.settings &&
                  props.form.settings.email &&
                  props.form.settings.email.managerMessage) ||
                '',
            },
            publicRegistrants: {
              enabled: !!(
                props.form.settings &&
                props.form.settings.publicRegistrants &&
                props.form.settings.publicRegistrants.enabled
              ),
              visibleFieldKeys: Array.isArray(
                props.form.settings &&
                  props.form.settings.publicRegistrants &&
                  props.form.settings.publicRegistrants.visibleFieldKeys
              )
                ? deepClone(
                    props.form.settings.publicRegistrants.visibleFieldKeys
                  )
                : [],
              columnLabels:
                props.form.settings &&
                props.form.settings.publicRegistrants &&
                props.form.settings.publicRegistrants.columnLabels
                  ? deepClone(
                      props.form.settings.publicRegistrants.columnLabels
                    )
                  : {},
            },
          },
          translations: {
            ...formData.value.translations,
            ...(props.form.translations || {}),
          },
        }

        // Date labels are managed per-field; no form-level month names handling
        // No-op for public name labels. Name/date labels are now configured per field.
        // Set counter based on existing fields
        const maxId = Math.max(
          ...formData.value.fields.map((f) => parseInt(f.id) || 0),
          0
        )
        fieldIdCounter.value = maxId + 1

        // Set counter based on existing team manager fields
        const maxTeamManagerId = Math.max(
          ...formData.value.settings.teamManagerFields.map(
            (f) => parseInt(f.id) || 0
          ),
          0
        )
        teamManagerFieldIdCounter.value = maxTeamManagerId + 1
      }
    }

    // Field operations
    const addField = () => {
      const newField = {
        id: `field_${fieldIdCounter.value++}`,
        type: 'text',
        label: '',
        name: '',
        placeholder: '',
        required: false,
        options: [],
        validation: {},
      }
      formData.value.fields.push(newField)
      selectedFieldIndex.value = formData.value.fields.length - 1
      // Don't mark new fields as manually edited
      manuallyEditedNames.value.delete(newField.id)
    }

    const removeField = (index) => {
      if (confirm(t('registration.deleteFieldConfirm'))) {
        formData.value.fields.splice(index, 1)
        if (selectedFieldIndex.value >= formData.value.fields.length) {
          selectedFieldIndex.value = formData.value.fields.length - 1
        }
      }
    }

    const duplicateField = (index) => {
      const field = formData.value.fields[index]
      const duplicated = {
        ...field,
        id: `field_${fieldIdCounter.value++}`,
        name: `${field.name}_copy`,
        label: `${field.label} (Copy)`,
      }
      formData.value.fields.splice(index + 1, 0, duplicated)
      selectedFieldIndex.value = index + 1
      // Don't mark duplicated fields as manually edited
      manuallyEditedNames.value.delete(duplicated.id)
    }

    const moveField = (index, direction) => {
      const newIndex = index + direction
      if (newIndex >= 0 && newIndex < formData.value.fields.length) {
        const field = formData.value.fields.splice(index, 1)[0]
        formData.value.fields.splice(newIndex, 0, field)
        selectedFieldIndex.value = newIndex
      }
    }

    const selectField = (index) => {
      selectedFieldIndex.value = index
      // Deselect team manager field when selecting an athlete field
      selectedTeamManagerFieldIndex.value = -1
    }

    // Field type operations
    const hasOptions = (type) => {
      return ['select', 'radio', 'checkbox'].includes(type)
    }

    const onFieldTypeChange = () => {
      if (selectedField.value) {
        if (
          hasOptions(selectedField.value.type) &&
          (!selectedField.value.options ||
            selectedField.value.options.length === 0)
        ) {
          selectedField.value.options = ['Option 1']
        }
        if (!hasOptions(selectedField.value.type)) {
          delete selectedField.value.options
        }
      }
    }

    const addOption = () => {
      if (selectedField.value && selectedField.value.options) {
        selectedField.value.options.push(
          `Option ${selectedField.value.options.length + 1}`
        )
      }
    }

    const removeOption = (index) => {
      if (selectedField.value && selectedField.value.options) {
        selectedField.value.options.splice(index, 1)
      }
    }

    // Enable manual editing of field name
    const enableManualEdit = () => {
      if (selectedField.value) {
        manuallyEditedNames.value.add(selectedField.value.id)
        // Focus the input after enabling
        nextTick(() => {
          const input = document.querySelector('.field-name-container input')
          if (input) {
            input.focus()
            input.select()
          }
        })
      }
    }

    // Disable manual editing and reset to auto-generated name
    const disableManualEdit = () => {
      if (selectedField.value) {
        manuallyEditedNames.value.delete(selectedField.value.id)
        // Regenerate name from label
        if (selectedField.value.label) {
          const baseName = generateFieldName(selectedField.value.label)
          selectedField.value.name = ensureUniqueFieldName(
            baseName,
            selectedField.value.id
          )
        }
      }
    }

    // Field name sanitization
    const sanitizeFieldName = () => {
      if (selectedField.value) {
        // Mark this field's name as manually edited (if not already)
        manuallyEditedNames.value.add(selectedField.value.id)

        const sanitized = selectedField.value.name
          .toLowerCase()
          // Allow Unicode letters, numbers, and underscores
          // Remove only problematic characters (spaces, punctuation, etc.)
          .replace(/[\s\-\.,:;!?@#$%^&*()+=\[\]{}|\\/<>"'`~]/g, '_')
          // Replace multiple underscores with single underscore
          .replace(/_{2,}/g, '_')
          // Remove leading/trailing underscores
          .replace(/^_|_$/g, '')

        // Ensure the sanitized name is unique
        selectedField.value.name = ensureUniqueFieldName(
          sanitized,
          selectedField.value.id
        )
      }
    }

    // Auto-generate field name from label
    const generateFieldName = (label) => {
      return (
        label
          .toLowerCase()
          // Allow Unicode letters, numbers, and underscores
          // Remove only problematic characters (spaces, punctuation, etc.)
          .replace(/[\s\-\.,:;!?@#$%^&*()+=\[\]{}|\\/<>"'`~]/g, '_')
          // Replace multiple underscores with single underscore
          .replace(/_{2,}/g, '_')
          // Remove leading/trailing underscores
          .replace(/^_|_$/g, '')
      )
    }

    // Track which fields have had their names manually edited
    const manuallyEditedNames = ref(new Set())

    // Function to ensure field name is unique
    const ensureUniqueFieldName = (baseName, excludeFieldId) => {
      const existingNames = formData.value.fields
        .filter((f) => f.id !== excludeFieldId)
        .map((f) => f.name)
        .filter((name) => name && name.trim() !== '')

      let uniqueName = baseName
      let counter = 1

      while (existingNames.includes(uniqueName)) {
        uniqueName = `${baseName}_${counter}`
        counter++
      }

      return uniqueName
    }

    // Ensure unique name within Team Manager fields (independent from athlete fields)
    const ensureUniqueTeamManagerFieldName = (baseName, excludeFieldId) => {
      const existingNames = (formData.value.settings.teamManagerFields || [])
        .filter((f) => f.id !== excludeFieldId)
        .map((f) => f.name)
        .filter((name) => name && name.trim() !== '')

      let uniqueName = baseName
      let counter = 1

      while (existingNames.includes(uniqueName)) {
        uniqueName = `${baseName}_${counter}`
        counter++
      }

      return uniqueName
    }

    // Function to handle field name auto-generation
    const updateFieldNameFromLabel = (field, label) => {
      if (
        field &&
        label &&
        field.type !== 'section' &&
        !manuallyEditedNames.value.has(field.id)
      ) {
        const baseName = generateFieldName(label)
        field.name = ensureUniqueFieldName(baseName, field.id)
      }
    }

    // Single watcher for field name auto-generation
    // Handles both label changes and field selection
    watch(
      () => ({
        field: selectedField.value,
        label: selectedField.value && selectedField.value.label,
      }),
      ({ field, label }) => {
        // Use nextTick to ensure DOM updates complete first
        nextTick(() => {
          updateFieldNameFromLabel(field, label)
        })
      },
      { immediate: true }
    )

    // Auto-generate team manager field names from label on selection/label changes
    watch(
      () => ({
        tmField: selectedTeamManagerField.value,
        label:
          selectedTeamManagerField.value &&
          selectedTeamManagerField.value.label,
      }),
      () => {
        nextTick(() => {
          updateTeamManagerFieldName()
        })
      },
      { immediate: true }
    )

    // Team Manager Field operations
    const addTeamManagerField = () => {
      const newField = {
        id: `team_field_${teamManagerFieldIdCounter.value++}`,
        type: 'text',
        label: '',
        name: '',
        placeholder: '',
        required: false,
        options: [],
        validation: {},
      }
      formData.value.settings.teamManagerFields.push(newField)
      selectedTeamManagerFieldIndex.value =
        formData.value.settings.teamManagerFields.length - 1
    }

    const removeTeamManagerField = (index) => {
      if (confirm(t('registration.deleteFieldConfirm'))) {
        formData.value.settings.teamManagerFields.splice(index, 1)
        if (
          selectedTeamManagerFieldIndex.value >=
          formData.value.settings.teamManagerFields.length
        ) {
          selectedTeamManagerFieldIndex.value =
            formData.value.settings.teamManagerFields.length - 1
        }
      }
    }

    const moveTeamManagerField = (index, direction) => {
      const newIndex = index + direction
      if (
        newIndex >= 0 &&
        newIndex < formData.value.settings.teamManagerFields.length
      ) {
        const field = formData.value.settings.teamManagerFields.splice(
          index,
          1
        )[0]
        formData.value.settings.teamManagerFields.splice(newIndex, 0, field)
        selectedTeamManagerFieldIndex.value = newIndex
      }
    }

    const selectTeamManagerField = (index) => {
      selectedTeamManagerFieldIndex.value = index
      // Deselect athlete field when selecting a team manager field
      selectedFieldIndex.value = -1
    }

    const onTeamManagerFieldTypeChange = () => {
      if (selectedTeamManagerField.value) {
        if (
          hasOptions(selectedTeamManagerField.value.type) &&
          (!selectedTeamManagerField.value.options ||
            selectedTeamManagerField.value.options.length === 0)
        ) {
          selectedTeamManagerField.value.options = ['Option 1']
        }
        if (!hasOptions(selectedTeamManagerField.value.type)) {
          delete selectedTeamManagerField.value.options
        }
      }
    }

    const addTeamManagerFieldOption = () => {
      if (
        selectedTeamManagerField.value &&
        selectedTeamManagerField.value.options
      ) {
        selectedTeamManagerField.value.options.push(
          `Option ${selectedTeamManagerField.value.options.length + 1}`
        )
      }
    }

    const removeTeamManagerFieldOption = (index) => {
      if (
        selectedTeamManagerField.value &&
        selectedTeamManagerField.value.options
      ) {
        selectedTeamManagerField.value.options.splice(index, 1)
      }
    }

    const sanitizeTeamManagerFieldName = () => {
      if (selectedTeamManagerField.value) {
        const sanitized = selectedTeamManagerField.value.name
          .toLowerCase()
          .replace(/[\s\-\.,:;!?@#$%^&*()+=\[\]{}|\\/<>"'`~]/g, '_')
          .replace(/_{2,}/g, '_')
          .replace(/^_|_$/g, '')

        selectedTeamManagerField.value.name = ensureUniqueTeamManagerFieldName(
          sanitized,
          selectedTeamManagerField.value.id
        )
      }
    }

    const updateTeamManagerFieldName = () => {
      if (
        selectedTeamManagerField.value &&
        selectedTeamManagerField.value.label
      ) {
        const baseName = generateFieldName(selectedTeamManagerField.value.label)
        selectedTeamManagerField.value.name = ensureUniqueTeamManagerFieldName(
          baseName,
          selectedTeamManagerField.value.id
        )
      }
    }

    // Field type labels
    const getFieldTypeLabel = (type) => {
      const typeLabels = {
        text: t('registration.fieldTypes.text'),
        email: t('registration.fieldTypes.email'),
        name: t('registration.fieldTypes.name'),
        number: t('registration.fieldTypes.number'),
        textarea: t('registration.fieldTypes.textarea'),
        select: t('registration.fieldTypes.select'),
        radio: t('registration.fieldTypes.radio'),
        checkbox: t('registration.fieldTypes.checkbox'),
        date: t('registration.fieldTypes.date'),
        time: t('registration.fieldTypes.time'),
        section: t('registration.fieldTypes.section'),
      }
      return typeLabels[type] || type
    }

    // Save form
    const saveForm = () => {
      if (isFormValid.value) {
        emit('save', formData.value)
      }
    }

    // Initialize on mount
    initializeForm()

    return {
      t,
      activeTab,
      // public registrants
      publicVisibleKeys,
      publicColumnLabels,
      publicFieldOptions,
      isKeySelectedElsewhere,
      addPublicKey,
      removePublicKey,
      movePublicKey,
      onColumnLabelChange,
      formData,
      selectedFieldIndex,
      selectedField,
      editingTargetField,
      isManuallyEdited,
      editorDuplicateNames,
      onEditorTypeChange,
      sanitizeEditorName,
      enableEditorManual,
      disableEditorManual,
      addEditorOption,
      removeEditorOption,
      selectedTeamManagerFieldIndex,
      selectedTeamManagerField,
      isEditing,
      isFormValid,

      duplicateFieldNames,
      manuallyEditedNames,
      addField,
      removeField,
      duplicateField,
      moveField,
      selectField,
      addTeamManagerField,
      removeTeamManagerField,
      moveTeamManagerField,
      selectTeamManagerField,
      onTeamManagerFieldTypeChange,
      addTeamManagerFieldOption,
      removeTeamManagerFieldOption,
      sanitizeTeamManagerFieldName,
      updateTeamManagerFieldName,
      hasOptions,
      onFieldTypeChange,
      addOption,
      removeOption,
      enableManualEdit,
      disableManualEdit,
      sanitizeFieldName,
      getFieldTypeLabel,
      saveForm,
      emailFieldOptions,
      managerEmailFieldOptions,
      isEmailConfigValid,
      isTestingConnection,
      connectionTestResult,
      testEmailConnection,
    }
  },
}
</script>

<style scoped>
/* Main Layout */
.form-builder-page {
  width: 100vw;
  height: 100vh;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.form-builder-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 2rem;
  background: white;
  border-bottom: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  z-index: 10;
}

.form-builder-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

/* Tab Navigation */
.tab-navigation {
  background: white;
  border-bottom: 1px solid #e9ecef;
  padding: 0 2rem;
  flex-shrink: 0;
}

.tab-container {
  display: flex;
  gap: 0;
  max-width: 1200px;
  margin: 0 auto;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border: none;
  background: transparent;
  color: #6c757d;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 3px solid transparent;
  position: relative;
}

.tab-btn:hover {
  color: #495057;
  background-color: #f8f9fa;
}

.tab-btn.active {
  color: #3498db;
  border-bottom-color: #3498db;
  background-color: #f8f9fa;
}

.tab-icon {
  font-size: 1.1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background-color: #3498db;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2980b9;
}

.btn-primary:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}

/* Main Content */
.form-builder-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
}

/* Content Sections */
.content-section {
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.content-section h4 {
  margin: 0 0 2rem 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

/* Form Grid Layouts */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;
}

.visible-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.visible-field-row {
  display: grid;
  grid-template-columns: auto 1fr 1fr auto;
  gap: 0.5rem;
  align-items: center;
}
.visible-field-row .drag-index {
  width: 2ch;
  text-align: right;
  color: #718096;
}
.visible-field-row .label-override {
  width: 100%;
}
.visible-field-row .row-actions {
  display: flex;
  gap: 0.25rem;
  justify-content: flex-end;
}
.actions-row {
  margin-top: 0.5rem;
}
.hint {
  color: #718096;
  font-size: 0.85rem;
  margin-top: 0.5rem;
}

.form-grid .full-width {
  grid-column: 1 / -1;
}

.basic-info-section,
.basic-public-section {
  max-width: 980px;
}

.basic-public-section {
  margin-top: 0.75rem;
}

.basic-info-section,
.basic-public-section {
  padding: 1.25rem 1.35rem;
  border: 1px solid #e9ecef;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.basic-info-section h4,
.basic-public-section h4 {
  margin-bottom: 1rem;
  font-size: 1.05rem;
}

.basic-info-grid {
  gap: 0.85rem 1rem;
}

.basic-info-grid .form-group {
  margin-bottom: 0.35rem;
}

.basic-info-grid .form-group.full-width {
  margin-bottom: 0.45rem;
}

.basic-info-grid textarea {
  min-height: 96px;
}

.basic-info-grid .help-text,
.basic-info-grid .hint {
  margin-top: 0.2rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.styling-group h5,
.translation-group h5 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1rem;
  font-weight: 600;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.translations-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem 1rem;
}

.translation-group {
  background: #fbfcfe;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem;
}

/* Fields Tab Layout */
.fields-tab {
  display: grid !important;
  grid-template-columns: 350px 1fr !important;
  gap: 2rem !important;
  padding: 2rem !important;
  height: 100%;
  align-items: stretch;
}

.fields-left-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.fields-right-panel {
  min-width: 0;
  display: flex;
}

.fields-panel,
.field-editor-panel {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.panel-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.field-type-badge {
  background: #3498db;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-header h4 {
  margin: 0;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  max-width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.9rem;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 0.5rem;
  font-weight: normal !important;
}

.checkbox-label input {
  width: auto !important;
  margin: 0;
}

.color-input {
  width: 60px !important;
  height: 40px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 2px;
  cursor: pointer;
}

.field-help {
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: block;
}

.field-error {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: block;
  font-weight: 500;
}

.form-group input.error,
.form-group textarea.error,
.form-group select.error {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.form-group input.auto-generated {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.field-name-container {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
}

.field-name-container input {
  flex: 1;
}

.edit-name-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  white-space: nowrap;
}

.btn-outline {
  background-color: transparent;
  border: 1px solid #6c757d;
  color: #6c757d;
}

.btn-outline:hover {
  background-color: #6c757d;
  color: white;
}

/* Empty Fields State */
.empty-fields {
  text-align: center;
  padding: 3rem 2rem;
  color: #6c757d;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.empty-fields h5 {
  margin: 0 0 0.5rem 0;
  color: #495057;
  font-size: 1.1rem;
}

.empty-fields p {
  margin: 0 0 1.5rem 0;
  color: #6c757d;
}

/* Fields List */
.fields-list {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 60vh;
  overflow-y: auto;
}

.field-item {
  background: #f8f9fa;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.field-item:hover {
  border-color: #3498db;
  background-color: #f0f8ff;
}

.field-item.active {
  border-color: #3498db;
  background-color: #e3f2fd;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.field-info {
  flex: 1;
}

.field-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
}

.field-meta {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.field-type {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  font-weight: 500;
  background: #e9ecef;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
}

.required-indicator {
  color: #dc3545;
  font-size: 0.75rem;
  font-weight: 600;
  background: #f8d7da;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
}

.field-actions {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}

/* Field Editor */
.editor-content {
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
}

.field-type-select {
  font-weight: 600;
  color: #3498db;
}

/* Options */
.options-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.option-item {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.option-item input {
  flex: 1;
  margin: 0;
  min-width: 0;
  box-sizing: border-box;
}

.add-option-btn {
  align-self: flex-start;
}

/* Validation Grid */
.validation-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.validation-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.validation-item label {
  font-size: 0.8rem;
  margin: 0;
  color: #6c757d;
}

.validation-item input {
  margin: 0;
}

/* Preview Tab */
.preview-container {
  max-width: 980px;
  margin: 0 auto;
}

.form-preview {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  max-height: 62vh;
  overflow-y: auto;
}

.preview-header {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.9rem;
  margin-bottom: 0.9rem;
}

.preview-header h3 {
  margin: 0 0 0.35rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.preview-header p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.preview-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.preview-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.8rem;
}

.preview-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.required {
  color: #e74c3c;
}

.preview-input input,
.preview-input textarea,
.preview-input select {
  width: 100%;
  max-width: 100%;
  padding: 0.6rem 0.7rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background-color: #f8f9fa;
  box-sizing: border-box;
  color: #6c757d;
}

.radio-preview,
.checkbox-preview {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.radio-option,
.checkbox-option {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.4rem;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: #fff;
}

.radio-option input,
.checkbox-option input {
  width: auto !important;
  margin: 0;
  flex-shrink: 0;
}

.radio-option label,
.checkbox-option label {
  margin: 0;
  font-weight: 500;
  color: #495057;
  flex: 1;
}

.no-fields-preview,
.no-options-message {
  color: #6c757d;
  font-size: 0.9rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border: 1px dashed #ced4da;
  border-radius: 4px;
  text-align: center;
}

.no-fields-preview p {
  margin: 0;
}

.section-preview {
  margin: 0.4rem 0;
  padding: 0.2rem 0;
}

.section-divider {
  height: 2px;
  background: #3498db;
  margin-bottom: 0.7rem;
  border-radius: 1px;
}

.section-title {
  margin: 0 0 0.4rem 0;
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 700;
  border-left: 3px solid #3498db;
  padding-left: 0.75rem;
}

.section-description {
  margin: 0;
  color: #6c757d;
  font-size: 0.88rem;
  line-height: 1.4;
  padding-left: 0.75rem;
}

.preview-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1rem;
  padding-top: 0.9rem;
  border-top: 1px solid #dee2e6;
}

.preview-submit-btn,
.preview-reset-btn {
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: not-allowed;
  opacity: 0.9;
}

.preview-submit-btn {
  background: #3498db;
  color: #fff;
}

.preview-reset-btn {
  background: #6c757d;
  color: #fff;
}

/* Buttons */
.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-sm {
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
}

.btn-primary {
  background-color: #3498db;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2980b9;
}

.btn-primary:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 3px;
  font-size: 0.8rem;
  color: #6c757d;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  background-color: #e9ecef;
  color: #495057;
}

.btn-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-icon.btn-danger {
  color: #dc3545;
}

.btn-icon.btn-danger:hover {
  background-color: #f8d7da;
  color: #721c24;
}

.help-text {
  font-size: 0.85rem;
  color: #6c757d;
  margin-top: 0.25rem;
  margin-bottom: 0;
  line-height: 1.4;
}

.form-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.5;
  resize: vertical;
  min-height: 120px;
}

.form-textarea:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-textarea:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.placeholder-legend {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.placeholder-legend h5 {
  margin: 0 0 0.75rem 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
}

.placeholder-grid {
  display: grid;
  gap: 0.5rem;
}

.placeholder-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.85rem;
}

.placeholder-item code {
  background-color: #e9ecef;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  color: #495057;
  min-width: 120px;
  text-align: center;
}

.placeholder-item span {
  color: #6c757d;
}

.email-settings-section {
  max-width: 980px;
}

.email-settings-grid {
  gap: 0.9rem 1rem;
}

.email-settings-grid .form-group {
  margin-bottom: 0.35rem;
}

.email-settings-grid .form-group.full-width {
  margin-bottom: 0.5rem;
}

.email-subsection-title {
  margin: 0.25rem 0 0.1rem 0;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid #e9ecef;
  color: #2c3e50;
  font-size: 0.95rem;
  font-weight: 700;
}

.email-settings-grid .help-text {
  margin-top: 0.2rem;
}

.email-settings-grid .placeholder-legend {
  margin-top: 0.6rem;
  padding: 0.75rem;
}

.smtp-settings-card,
.sender-settings-card,
.athlete-template-card,
.manager-template-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.65rem 0.75rem 0.55rem;
  background: #fff;
}

.smtp-settings-card .email-subsection-title,
.sender-settings-card .email-subsection-title,
.athlete-template-card .email-subsection-title,
.manager-template-card .email-subsection-title {
  margin-top: 0;
}

.smtp-compact-grid,
.sender-compact-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.55rem 0.8rem;
}

.smtp-compact-grid .form-group,
.sender-compact-grid .form-group {
  margin-bottom: 0;
}

.smtp-compact-grid .full-span {
  grid-column: 1 / -1;
}

.compact-checkbox-label {
  margin-top: 0.15rem;
}

.connection-result {
  margin-top: 0.55rem;
  padding: 0.6rem 0.7rem;
  border-radius: 8px;
  border: 1px solid transparent;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.connection-result-title {
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.2;
}

.connection-result-message {
  font-size: 0.84rem;
  line-height: 1.35;
}

.connection-result.connection-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #b6dfc2;
}

.connection-result.connection-error {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #efb3b8;
}

.team-manager-settings {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem;
  margin-top: 1rem;
}

.team-manager-settings h5 {
  margin: 0 0 0.5rem 0;
  color: #495057;
  font-size: 1rem;
  font-weight: 600;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Team Manager Fields Builder */
.team-manager-fields-builder {
  margin-top: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  background-color: #f8f9fa;
}

.fields-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-weight: 600;
  color: #495057;
}

.empty-team-fields {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

.team-manager-fields-list {
  margin-bottom: 1rem;
}

.team-field-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.team-field-item:hover {
  border-color: #3498db;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.team-field-item.active {
  border-color: #3498db;
  background-color: #f8f9fa;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
}

.team-field-editor {
  margin-top: 1rem;
  padding: 1rem;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

.team-field-editor h6 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1rem;
  font-weight: 600;
}

/* Results-style floating save/cancel controls */
.floating-buttons {
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  gap: 10px;
  z-index: 2100;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1),
    rgba(255, 255, 255, 0.2)
  );
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
  padding: 10px;
}

.floating-buttons .btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  transition: all 0.3s ease;
}

.floating-buttons .btn-primary {
  background-color: rgba(39, 174, 96, 0.9);
  color: #fff;
  box-shadow: 0 4px 6px rgba(39, 174, 96, 0.3);
}

.floating-buttons .btn-primary:hover:not(:disabled) {
  background-color: rgba(46, 204, 113, 0.9);
  box-shadow: 0 6px 8px rgba(39, 174, 96, 0.4);
}

.floating-buttons .btn-primary:disabled {
  background-color: rgba(127, 140, 141, 0.85);
  box-shadow: none;
  cursor: not-allowed;
}

.floating-buttons .btn-cancel {
  background-color: rgba(231, 76, 60, 0.9);
  color: #fff;
  box-shadow: 0 4px 6px rgba(231, 76, 60, 0.3);
}

.floating-buttons .btn-cancel:hover {
  background-color: rgba(192, 57, 43, 0.9);
  box-shadow: 0 6px 8px rgba(231, 76, 60, 0.4);
}

/* Results-style visual overrides */
.form-builder-page {
  background: #f0f2f5;
}

.form-builder-header,
.tab-navigation,
.content-section,
.fields-panel,
.field-editor-panel {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-builder-header h3 {
  color: #2c3e50;
}

.tab-btn {
  border-bottom-width: 2px;
  border-bottom-color: transparent;
}

.tab-btn.active {
  color: #2c3e50;
  border-bottom-color: #3498db;
  background-color: #f8f9fa;
}

.tab-btn:hover {
  background-color: #e9ecef;
}

.btn-primary {
  background-color: #3498db;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2980b9;
}

.field-type-badge {
  background: #3498db;
}

.field-item:hover,
.field-item.active,
.team-field-item:hover,
.team-field-item.active {
  border-color: #3498db;
}

.field-item.active,
.team-field-item.active {
  box-shadow: 0 2px 6px rgba(52, 152, 219, 0.2);
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus,
.form-textarea:focus {
  border-color: #3498db;
  box-shadow: 0 0 0 0.15rem rgba(52, 152, 219, 0.2);
}

.panel-header {
  background: #f8f9fa;
}

.section-divider {
  background: #3498db;
}

.section-title {
  border-left-color: #3498db;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .form-builder-content {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .form-builder-content {
    grid-template-columns: 1fr;
    padding: 1rem;
  }

  .form-builder-header {
    padding: 1rem;
  }

  .form-builder-header h3 {
    font-size: 1.25rem;
  }

  .header-actions {
    gap: 0.5rem;
  }

  .btn {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }

  .validation-rules {
    grid-template-columns: 1fr;
  }

  .basic-info-grid {
    grid-template-columns: 1fr;
  }

  .basic-info-section,
  .basic-public-section {
    padding: 1rem;
  }

  .floating-buttons {
    flex-direction: column;
    right: 10px;
    bottom: 10px;
  }

  .floating-buttons .btn {
    font-size: 0.9rem;
    padding: 8px 16px;
  }
}
</style>
